import type { JobChangeConfig, TriggerConfig } from '@/types/custom-playbooks';

/** The most Contacts one job change trigger watches. The save refuses more
 *  (`job_change_watch_limit`). */
export const JOB_CHANGE_WATCH_LIMIT = 10_000;

/** The most Salesforce reports one job change watches. */
export const JOB_CHANGE_REPORTS_MAX = 10;

/** The longest relationship a report tags its Contacts with. */
export const JOB_CHANGE_RELATIONSHIP_MAX = 255;

// 00O, then 12 characters; the 18-character form adds a 3-character suffix.
const REPORT_ID = /^00O[a-zA-Z0-9]{12}(?:[a-zA-Z0-9]{3})?$/;
const REPORT_KEYS = new Set(['id', 'name', 'relationship']);
const REPORT_NAME_MAX = 200;

/** A relationship the server takes: 1 to 255 characters, without a space
 *  at either end, a line break or a tab. */
function relationshipReady(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    value.length >= 1 &&
    value.length <= JOB_CHANGE_RELATIONSHIP_MAX &&
    value.trim() === value &&
    ![...value].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127)
  );
}

/** Whether two Salesforce ids name one record: an 18-character id is its
 *  15-character id plus a suffix. */
export function sameSalesforceId(a: string, b: string): boolean {
  return a.slice(0, 15) === b.slice(0, 15);
}

/** Legacy graphs read with current defaults and change only on explicit save. */
export function jobChangeValue(
  config: Partial<TriggerConfig>
): JobChangeConfig {
  const scope = config.jobChange?.scope ?? 'all';
  const conditions = config.jobChange?.conditions;
  const reports = config.jobChange?.reports;
  return {
    // Job change has no Attio arm; an Attio trigger reads as Salesforce.
    crmType:
      (config.jobChange?.crmType ?? config.crm?.crmType) === 'hubspot'
        ? 'hubspot'
        : 'salesforce',
    scope,
    listId: config.jobChange?.listId ?? null,
    listName: config.jobChange?.listName ?? null,
    ...(scope === 'filter' && conditions ? { conditions } : {}),
    ...(scope === 'reports' && reports ? { reports } : {}),
    watch: config.jobChange?.watch ?? { company: true, title: false },
    freshness: config.jobChange?.freshness ?? 'monthly',
  };
}

/** Conditions belong to scope 'filter' only and reports to scope 'reports';
 *  neither watches a list. */
function scopedJobChange(jobChange: JobChangeConfig): JobChangeConfig {
  const { conditions, reports, ...rest } = jobChange;
  if (rest.scope === 'filter')
    return {
      ...rest,
      listId: null,
      listName: null,
      ...(conditions ? { conditions } : {}),
    };
  if (rest.scope === 'reports')
    return {
      ...rest,
      listId: null,
      listName: null,
      ...(reports ? { reports } : {}),
    };
  return rest;
}

/** Whether a reports watch is one the server takes (cleanse-api
 *  providers/agents/job_change_trigger.py): Salesforce, 1 to 10 distinct
 *  reports, each with a relationship, and no list or conditions beside
 *  them. */
export function jobChangeReportsReady(jobChange: JobChangeConfig): boolean {
  const reports = jobChange.reports;
  if (
    jobChange.crmType !== 'salesforce' ||
    jobChange.listId ||
    jobChange.conditions ||
    !Array.isArray(reports) ||
    reports.length < 1 ||
    reports.length > JOB_CHANGE_REPORTS_MAX
  )
    return false;
  return reports.every(
    (report, index) =>
      !!report &&
      typeof report === 'object' &&
      Object.keys(report).every((key) => REPORT_KEYS.has(key)) &&
      typeof report.id === 'string' &&
      REPORT_ID.test(report.id) &&
      (report.name == null ||
        (typeof report.name === 'string' &&
          report.name.length <= REPORT_NAME_MAX)) &&
      relationshipReady(report.relationship) &&
      !reports
        .slice(0, index)
        .some((earlier) => sameSalesforceId(earlier.id, report.id))
  );
}

export function saveJobChangeConfig(
  config: TriggerConfig,
  jobChange: JobChangeConfig
): TriggerConfig {
  const next: TriggerConfig = {
    ...config,
    source: 'job_change',
    kind: 'job_change',
    jobChange: scopedJobChange(jobChange),
    // Job changes are checked every 30 minutes: the API refuses a faster
    // interval, so a saved everyMinutes goes.
    schedule: { frequency: 'continuous' },
  };
  delete next.crm;
  delete next.rerunAfterDays;
  delete next.skipTouchedWithinDays;
  return next;
}

// HubSpot search limits for a job change on matching Contacts, as cleanse-api
// providers/agents/job_change_trigger.py (contact_conditions) checks them:
// every group also carries the recheck's hs_object_id filter.
const HUBSPOT_GROUPS_MAX = 5;
const HUBSPOT_FILTERS_MAX = 4;
const HUBSPOT_SEARCH_FILTERS_MAX = 18;
const DAY_MS = 86_400_000;
const HUBSPOT_OPERATORS: Record<string, string> = {
  equals: 'EQ',
  not_equals: 'NEQ',
  contains: 'CONTAINS_TOKEN',
  not_contains: 'NOT_CONTAINS_TOKEN',
  greater_than: 'GT',
  less_than: 'LT',
  greater_than_or_equal: 'GTE',
  less_than_or_equal: 'LTE',
  in: 'IN',
  not_in: 'NOT_IN',
  between: 'BETWEEN',
};
const DAY_OPERATORS = new Set([
  'EQ',
  'NEQ',
  'GT',
  'GTE',
  'LT',
  'LTE',
  'BETWEEN',
  'IN',
  'NOT_IN',
]);
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE =
  /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** A day's midnight UTC in ms, or null for a value that isn't a whole day. */
function dayStart(value: unknown): number | null {
  if (typeof value !== 'string' || !ISO_DAY.test(value.trim())) return null;
  const [y, m, d] = value.trim().split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

function rowValues(hsOperator: string, value: unknown): unknown[] {
  if (hsOperator === 'IN' || hsOperator === 'NOT_IN')
    return (Array.isArray(value) ? value : [value]).filter(
      (v) => v !== null && v !== undefined && v !== ''
    );
  if (hsOperator === 'BETWEEN') {
    if (Array.isArray(value)) return value.length === 2 ? value : [];
    if (value && typeof value === 'object') {
      const bound = (keys: string[]) =>
        keys
          .map((key) => (value as Record<string, unknown>)[key])
          .find((v) => v !== null && v !== undefined && v !== '');
      return [bound(['from', 'start', 'low']), bound(['to', 'end', 'high'])];
    }
    return [];
  }
  return [value];
}

/** One condition as HubSpot search filters: its alternatives (OR), each the
 *  number of filters it ANDs. A day compares the whole UTC day. */
function hubspotAlternatives(row: unknown): number[] {
  if (!row || typeof row !== 'object') return [1];
  const c = row as Record<string, unknown>;
  const hsOperator =
    typeof c.operator === 'string' ? HUBSPOT_OPERATORS[c.operator] : undefined;
  if (!hsOperator) return [1];
  const saved = 'fieldType' in c ? c.fieldType : c.field_type;
  const fieldType = saved === null ? 'none' : String(saved ?? '').toLowerCase();
  const values = rowValues(hsOperator, c.value);
  const dated =
    ['date', 'datetime'].includes(fieldType) ||
    (['', 'none'].includes(fieldType) &&
      !hsOperator.endsWith('CONTAINS_TOKEN') &&
      values.length > 0 &&
      values.every((v) => typeof v === 'string' && ISO_DATE.test(v.trim())));
  if (
    !dated ||
    !DAY_OPERATORS.has(hsOperator) ||
    values.some(
      (v) =>
        v === null ||
        v === undefined ||
        !(/^-?\d+$/.test(String(v)) || ISO_DATE.test(String(v).trim()))
    )
  )
    return [1];
  const days = [
    ...new Set(values.map(dayStart).filter((d): d is number => d !== null)),
  ].sort((a, b) => a - b);
  const listed = values.some((v) => dayStart(v) === null) ? 1 : 0;
  if (hsOperator === 'IN')
    return listed || days.length
      ? [...(listed ? [1] : []), ...days.map(() => 2)]
      : [1];
  if (hsOperator === 'NOT_IN') {
    if (!days.length) return [1];
    const out = [listed + 1];
    days.forEach((start, i) => {
      if (i > 0 && days[i - 1] + DAY_MS < start) out.push(listed + 2);
    });
    return [...out, listed + 1];
  }
  if (!days.length) return [1];
  if (hsOperator === 'BETWEEN' || hsOperator === 'EQ') return [2];
  if (hsOperator === 'NEQ') return [1, 1];
  return [1];
}

/** Whether these conditions fit the one HubSpot search a job change on
 *  matching Contacts runs: Match any as 5 groups at most, Match all as 4
 *  filters at most. */
export function fitsHubSpotJobChangeSearch(
  conditions: JobChangeConfig['conditions']
): boolean {
  const rows = conditions?.conditions ?? [];
  const alternatives = rows.map(hubspotAlternatives);
  if (String(conditions?.operator).toUpperCase() === 'OR') {
    const groups = alternatives.flat();
    return (
      groups.length <= HUBSPOT_GROUPS_MAX &&
      groups.every((width) => width <= HUBSPOT_FILTERS_MAX) &&
      groups.reduce((sum, width) => sum + width, 0) + groups.length <=
        HUBSPOT_SEARCH_FILTERS_MAX
    );
  }
  const groups = alternatives.reduce((n, alts) => n * alts.length, 1);
  const widest = alternatives.reduce((n, alts) => n + Math.max(...alts), 0);
  // Every group holds one alternative of each condition.
  const filters = alternatives.reduce(
    (sum, alts) =>
      sum + alts.reduce((s, width) => s + width, 0) * (groups / alts.length),
    0
  );
  return (
    groups <= HUBSPOT_GROUPS_MAX &&
    widest <= HUBSPOT_FILTERS_MAX &&
    filters + groups <= HUBSPOT_SEARCH_FILTERS_MAX
  );
}

/** HubSpot's limit line once the conditions hold no more: null while a
 *  condition still fits. */
export function hubspotJobChangeLimit(
  conditions: JobChangeConfig['conditions']
): string | null {
  const any = String(conditions?.operator).toUpperCase() === 'OR';
  const next = {
    operator: any ? ('OR' as const) : ('AND' as const),
    conditions: [
      ...(conditions?.conditions ?? []),
      { id: 'next', field: 'next', operator: 'exists', value: undefined },
    ],
  };
  if (fitsHubSpotJobChangeSearch(next)) return null;
  return `HubSpot allows up to ${any ? HUBSPOT_GROUPS_MAX : HUBSPOT_FILTERS_MAX} here`;
}
